/**
 * Pure renderer for a single drawing shape on the tactical pitch SVG.
 * Supports: strokes (line/arrow/run_arrow/dribble_arrow/free/zone),
 * annotations (text), and training equipment (goal/mini_goal/cone/
 * flat_marker/ring/hurdle/mannequin/ladder/ball).
 */
export function PitchShape({ shape, k }) {
  if (!shape) return null;

  // ---------- Stroke types ----------
  if (shape.type === "line") {
    const [a, b] = shape.points;
    return (
      <line
        key={k}
        x1={a.x} y1={a.y} x2={b.x} y2={b.y}
        stroke={shape.color} strokeWidth="0.5" strokeLinecap="round"
      />
    );
  }

  if (shape.type === "arrow") {
    const [a, b] = shape.points;
    return (
      <line
        key={k}
        x1={a.x} y1={a.y} x2={b.x} y2={b.y}
        stroke={shape.color} strokeWidth="0.55" strokeLinecap="round"
        markerEnd={`url(#arrow-${shape.color.replace("#", "")})`}
      />
    );
  }

  if (shape.type === "run_arrow") {
    // Dashed straight arrow → "corsa"
    const [a, b] = shape.points;
    return (
      <line
        key={k}
        x1={a.x} y1={a.y} x2={b.x} y2={b.y}
        stroke={shape.color} strokeWidth="0.5" strokeLinecap="round"
        strokeDasharray="1.5,1.2"
        markerEnd={`url(#arrow-${shape.color.replace("#", "")})`}
      />
    );
  }

  if (shape.type === "dribble_arrow") {
    // Zigzag interpolation between start/end for a dribbling style arrow
    const [a, b] = shape.points;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const px = -uy; const py = ux; // perpendicular
    const amp = Math.min(2.5, Math.max(1.2, len / 12));
    const seg = 3;
    const steps = Math.max(2, Math.floor(len / seg));
    const path = [`M ${a.x} ${a.y}`];
    for (let i = 1; i <= steps; i += 1) {
      const t = i / steps;
      const cx = a.x + dx * t + px * amp * (i % 2 === 0 ? -1 : 1);
      const cy = a.y + dy * t + py * amp * (i % 2 === 0 ? -1 : 1);
      path.push(`L ${cx} ${cy}`);
    }
    path.push(`L ${b.x} ${b.y}`);
    return (
      <path
        key={k}
        d={path.join(" ")}
        fill="none"
        stroke={shape.color} strokeWidth="0.45" strokeLinecap="round" strokeLinejoin="round"
        markerEnd={`url(#arrow-${shape.color.replace("#", "")})`}
      />
    );
  }

  if (shape.type === "free") {
    const d = shape.points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
    return <path key={k} d={d} fill="none" stroke={shape.color} strokeWidth="0.4" strokeLinecap="round" strokeLinejoin="round" />;
  }

  if (shape.type === "zone") {
    const [a, b] = shape.points;
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const w = Math.abs(b.x - a.x);
    const h = Math.abs(b.y - a.y);
    return (
      <rect
        key={k}
        x={x} y={y} width={w} height={h}
        fill={shape.color} fillOpacity="0.22"
        stroke={shape.color} strokeOpacity="0.7"
        strokeWidth="0.3" strokeDasharray="0.8,0.6"
      />
    );
  }

  // ---------- Text annotation ----------
  if (shape.type === "text") {
    return (
      <text
        key={k} x={shape.x} y={shape.y}
        fontSize="3" fontWeight="700"
        fill={shape.color}
        stroke={shape.color === "#ffffff" ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.4)"}
        strokeWidth="0.15"
        style={{ pointerEvents: "none", userSelect: "none" }}
      >
        {shape.text}
      </text>
    );
  }

  // ---------- Equipment ----------
  if (shape.type === "goal") {
    const w = 12, h = 4;
    const x = shape.x - w / 2, y = shape.y - h / 2;
    const fillColor = shape.color || "#ffffff";
    return (
      <g key={k} transform={`rotate(${shape.rotation || 0}, ${shape.x}, ${shape.y})`}>
        <rect x={x} y={y} width={w} height={h} fill="none" stroke={fillColor} strokeWidth="0.5" />
        <line x1={x} y1={y} x2={x + w} y2={y + h} stroke={fillColor} strokeWidth="0.15" opacity="0.6" />
        <line x1={x + w} y1={y} x2={x} y2={y + h} stroke={fillColor} strokeWidth="0.15" opacity="0.6" />
        <line x1={x + w / 2} y1={y} x2={x + w / 2} y2={y + h} stroke={fillColor} strokeWidth="0.15" opacity="0.6" />
        <line x1={x} y1={y + h / 2} x2={x + w} y2={y + h / 2} stroke={fillColor} strokeWidth="0.15" opacity="0.6" />
        <circle cx={x} cy={y} r="0.5" fill={fillColor} />
        <circle cx={x + w} cy={y} r="0.5" fill={fillColor} />
      </g>
    );
  }

  if (shape.type === "mini_goal") {
    const w = 6, h = 2.2;
    const x = shape.x - w / 2, y = shape.y - h / 2;
    const fillColor = shape.color || "#ffffff";
    return (
      <g key={k}>
        <rect x={x} y={y} width={w} height={h} fill="none" stroke={fillColor} strokeWidth="0.45" />
        <line x1={x + w / 3} y1={y} x2={x + w / 3} y2={y + h} stroke={fillColor} strokeWidth="0.15" opacity="0.6" />
        <line x1={x + (2 * w) / 3} y1={y} x2={x + (2 * w) / 3} y2={y + h} stroke={fillColor} strokeWidth="0.15" opacity="0.6" />
      </g>
    );
  }

  if (shape.type === "cone") {
    const color = shape.color || "#f97316";
    return (
      <g key={k}>
        <polygon
          points={`${shape.x},${shape.y - 2} ${shape.x - 1.4},${shape.y + 1.4} ${shape.x + 1.4},${shape.y + 1.4}`}
          fill={color} stroke="#7c2d12" strokeWidth="0.15"
        />
        <line
          x1={shape.x - 1.1} y1={shape.y + 0.2}
          x2={shape.x + 1.1} y2={shape.y + 0.2}
          stroke="#ffffff" strokeWidth="0.3" opacity="0.85"
        />
        <ellipse cx={shape.x} cy={shape.y + 1.4} rx="1.4" ry="0.35" fill={color} stroke="#7c2d12" strokeWidth="0.1" />
      </g>
    );
  }

  if (shape.type === "flat_marker") {
    const color = shape.color || "#fbbf24";
    return (
      <g key={k}>
        <ellipse cx={shape.x} cy={shape.y} rx="1.3" ry="0.45" fill={color} stroke="#92400e" strokeWidth="0.12" />
      </g>
    );
  }

  if (shape.type === "ring") {
    const color = shape.color || "#22c55e";
    return (
      <g key={k}>
        <ellipse cx={shape.x} cy={shape.y} rx="1.6" ry="0.6" fill="none" stroke={color} strokeWidth="0.4" />
      </g>
    );
  }

  if (shape.type === "hurdle") {
    const color = shape.color || "#ef4444";
    const w = 2.6;
    return (
      <g key={k}>
        <line x1={shape.x - w / 2} y1={shape.y + 0.4} x2={shape.x - w / 2} y2={shape.y - 1.2} stroke={color} strokeWidth="0.25" />
        <line x1={shape.x + w / 2} y1={shape.y + 0.4} x2={shape.x + w / 2} y2={shape.y - 1.2} stroke={color} strokeWidth="0.25" />
        <line x1={shape.x - w / 2 - 0.3} y1={shape.y - 1.2} x2={shape.x + w / 2 + 0.3} y2={shape.y - 1.2} stroke={color} strokeWidth="0.55" />
        <line x1={shape.x - w / 2 - 0.3} y1={shape.y + 0.4} x2={shape.x - w / 2 + 0.5} y2={shape.y + 0.4} stroke={color} strokeWidth="0.3" />
        <line x1={shape.x + w / 2 + 0.3} y1={shape.y + 0.4} x2={shape.x + w / 2 - 0.5} y2={shape.y + 0.4} stroke={color} strokeWidth="0.3" />
      </g>
    );
  }

  if (shape.type === "mannequin") {
    const color = shape.color || "#1e293b";
    return (
      <g key={k}>
        <circle cx={shape.x} cy={shape.y - 1.5} r="0.7" fill={color} />
        <rect x={shape.x - 0.7} y={shape.y - 0.8} width="1.4" height="2.2" rx="0.3" fill={color} />
        <ellipse cx={shape.x} cy={shape.y + 1.6} rx="1.4" ry="0.35" fill={color} stroke="#0f172a" strokeWidth="0.12" />
      </g>
    );
  }

  if (shape.type === "ladder") {
    const color = shape.color || "#facc15";
    const w = 2.4;
    const h = shape.length || 6;
    const rungs = Math.max(3, Math.floor(h / 1.2));
    return (
      <g key={k} transform={`translate(${shape.x - w / 2}, ${shape.y - h / 2})`}>
        <line x1="0" y1="0" x2="0" y2={h} stroke={color} strokeWidth="0.3" />
        <line x1={w} y1="0" x2={w} y2={h} stroke={color} strokeWidth="0.3" />
        {Array.from({ length: rungs }).map((_, i) => {
          const yy = (h / (rungs - 1)) * i;
          return <line key={i} x1="0" y1={yy} x2={w} y2={yy} stroke={color} strokeWidth="0.3" />;
        })}
      </g>
    );
  }

  if (shape.type === "ball") {
    return (
      <g key={k}>
        <circle cx={shape.x} cy={shape.y} r="1.05" fill="#ffffff" stroke="#0f172a" strokeWidth="0.2" />
        <path
          d={`M ${shape.x - 0.5} ${shape.y - 0.3} L ${shape.x + 0.5} ${shape.y - 0.3} L ${shape.x + 0.4} ${shape.y + 0.4} L ${shape.x - 0.4} ${shape.y + 0.4} Z`}
          fill="#0f172a"
        />
      </g>
    );
  }

  return null;
}

/**
 * Static pitch lines (penalty box, center circle, etc).
 */
export function PitchLines() {
  return (
    <g stroke="rgba(255,255,255,0.7)" strokeWidth="0.3" fill="none">
      <rect x="2" y="2" width="96" height="96" />
      <line x1="2" y1="50" x2="98" y2="50" />
      <circle cx="50" cy="50" r="9" />
      <circle cx="50" cy="50" r="0.6" fill="white" />
      <rect x="25" y="2" width="50" height="14" />
      <rect x="35" y="2" width="30" height="6" />
      <rect x="25" y="84" width="50" height="14" />
      <rect x="35" y="92" width="30" height="6" />
      <circle cx="50" cy="14" r="0.6" fill="white" />
      <circle cx="50" cy="86" r="0.6" fill="white" />
    </g>
  );
}
